import { useState, useRef, useEffect, useCallback } from 'react';
import { query } from '../api';
import { confirmVN } from './Dialog';

/**
 * Gửi THÔNG BÁO lên màn hình máy khách.
 *
 * Apple MDM (iOS) KHÔNG có lệnh hiện banner — muốn banner phải cài app riêng trên máy.
 * Cách duy nhất không cần app: ĐẶT ẢNH NỀN (lệnh Settings/Wallpaper, supervised-only)
 * cho CẢ màn khoá + màn chính, kèm khoá không cho khách tự đổi lại.
 *
 * Ảnh được vẽ NGAY TẠI ĐÂY bằng canvas: ô xem trước chính là ảnh gửi xuống máy.
 * Chỉ admin / điều hành / quản lý mở được modal này (nơi gọi đã chặn sẵn).
 */

// Khung ảnh nền — tỉ lệ iPhone hiện đại (~19.5:9), dùng chung cho mọi máy.
const W = 1170;
const H = 2532;

export const MAX_NOTIFY_TITLE = 40;
export const MAX_NOTIFY_BODY = 160;

// Bo góc thủ công (ctx.roundRect chưa có trên mọi trình duyệt).
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Xuống dòng theo bề rộng thật của chữ (đo bằng chính font đang set).
function wrapText(ctx, text, maxWidth) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Vẽ ảnh thông báo: nền tối + khối chữ căn giữa. Đặt giữa màn hình để trên màn KHOÁ
// đọc rõ (không đụng đồng hồ ở trên), còn màn CHÍNH thì nằm xen giữa các hàng icon.
function drawNotify(canvas, title, body) {
  const ctx = canvas.getContext('2d');
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#0B1220');
  bg.addColorStop(0.55, '#152740');
  bg.addColorStop(1, '#0B1220');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const pad = 66;
  const px = 76;
  const pw = W - px * 2;
  const maxW = pw - pad * 2;

  const titleFont = '700 78px "Be Vietnam Pro", system-ui, sans-serif';
  const bodyFont = '400 48px "Be Vietnam Pro", system-ui, sans-serif';

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  ctx.font = titleFont;
  const tLines = wrapText(ctx, title, maxW);
  ctx.font = bodyFont;
  const bLines = wrapText(ctx, body, maxW);

  const tLH = 100;
  const bLH = 72;
  const gap = tLines.length && bLines.length ? 34 : 0;
  const contentH = tLines.length * tLH + gap + bLines.length * bLH;
  const ph = Math.max(contentH + pad * 2, 260);
  const py = Math.round((H - ph) / 2);

  ctx.fillStyle = 'rgba(4,10,20,.58)';
  roundRect(ctx, px, py, pw, ph, 54);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.20)';
  ctx.lineWidth = 3;
  ctx.stroke();

  let y = py + pad;
  ctx.fillStyle = '#FFFFFF';
  ctx.font = titleFont;
  for (const l of tLines) { ctx.fillText(l, W / 2, y); y += tLH; }
  y += gap;
  ctx.fillStyle = 'rgba(255,255,255,.90)';
  ctx.font = bodyFont;
  for (const l of bLines) { ctx.fillText(l, W / 2, y); y += bLH; }
}

export default function NotifyModal({ device, onClose, showToast }) {
  const batch = !device;                    // không có device = gửi hàng loạt
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { on, title, at } — chỉ khi gửi 1 máy; null = đang tải
  const canvasRef = useRef(null);
  const hasNotify = !!(status && status.on); // máy đang có thông báo -> mới gỡ được

  const redraw = useCallback(() => {
    if (canvasRef.current) drawNotify(canvasRef.current, title, body);
  }, [title, body]);

  // Vẽ lại mỗi khi đổi nội dung. Chờ font tải xong ở lần đầu để đo chữ cho đúng.
  useEffect(() => {
    redraw();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(redraw).catch(() => {});
  }, [redraw]);

  // Trạng thái thông báo đang đặt trên máy (để hiện nút Gỡ).
  useEffect(() => {
    if (batch) return;
    query.getNotify(device.udid).then(setStatus).catch(() => {});
  }, [batch, device]);

  const toBlob = () =>
    new Promise((resolve, reject) => {
      canvasRef.current.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('không tạo được ảnh'))),
        'image/jpeg',
        0.9,
      );
    });

  async function send() {
    if (!title.trim() && !body.trim()) {
      showToast('Nhập tiêu đề hoặc nội dung trước khi gửi', 'error', '❌');
      return;
    }
    if (batch) {
      const ok = await confirmVN(
        'Ảnh nền của MỌI máy trong phạm vi sẽ bị thay và khoá không cho khách đổi lại. Tiếp tục?',
        { title: 'Gửi thông báo cho tất cả máy?', okText: 'Gửi tất cả' },
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const blob = await toBlob();
      if (batch) {
        const r = await query.notifyAllDevices({ title: title.trim(), blob });
        showToast(`Đã gửi thông báo tới ${r.sent ?? 0} máy — máy online nhận trước`, 'success', '📢');
      } else {
        await query.sendNotify(device.udid, { title: title.trim(), blob });
        showToast('Đã gửi thông báo xuống máy', 'success', '📢');
      }
      onClose();
    } catch (ex) {
      showToast((ex.body && ex.body.error) || ex.message, 'error', '❌');
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    const ok = await confirmVN(
      'Máy sẽ về ảnh nền trơn và khách đổi lại được. KHÔNG khôi phục được ảnh nền gốc của khách. Tiếp tục?',
      { title: 'Gỡ thông báo?', okText: 'Gỡ thông báo' },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await query.clearNotify(device.udid);
      showToast('Đã gỡ thông báo — máy về ảnh nền trơn', 'success', '🧹');
      onClose();
    } catch (ex) {
      showToast((ex.body && ex.body.error) || ex.message, 'error', '❌');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay open" onClick={(e) => { if (e.target.className === 'overlay open') onClose(); }}>
      <style>{NOTIFY_CSS}</style>
      <div className="modal notify-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">📢</div>
            <div>
              <div className="modal-device-name">
                {batch ? 'Gửi thông báo cho tất cả máy' : 'Gửi thông báo lên màn hình'}
              </div>
              <div className="modal-device-sub">
                {batch ? 'Mọi máy trong phạm vi của bạn' : (device.name || device.serial_number || device.udid)}
              </div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="tab-body">
          <div className="page-note" style={{ marginBottom: 12 }}>
            Thông báo được đặt làm <b>ảnh nền cả màn khoá và màn chính</b>, kèm <b>khoá không cho khách đổi lại</b>.
            Chỉ áp dụng cho <b>máy supervised</b> (máy cài tay sẽ báo lỗi).
          </div>

          {!batch && status && status.on && (
            <div className="page-note" style={{ marginBottom: 12 }}>
              🔔 Máy đang có thông báo{status.title ? <>: <b>{status.title}</b></> : null}.
            </div>
          )}

          <div className="notify-grid">
            <div className="notify-form">
              <label>
                <span>Tiêu đề <i>{title.length}/{MAX_NOTIFY_TITLE}</i></span>
                <input
                  value={title}
                  maxLength={MAX_NOTIFY_TITLE}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="VD: Quá hạn thanh toán"
                  autoFocus
                />
              </label>
              <label>
                <span>Nội dung <i>{body.length}/{MAX_NOTIFY_BODY}</i></span>
                <textarea
                  rows={5}
                  value={body}
                  maxLength={MAX_NOTIFY_BODY}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="VD: Vui lòng liên hệ cửa hàng 0900 000 000 để thanh toán."
                />
              </label>
              <div className="notify-hint">Giới hạn ký tự để chữ đủ lớn, đọc rõ trên màn hình máy.</div>
            </div>

            <div className="notify-preview">
              <div className="notify-preview-label">Xem trước</div>
              <canvas ref={canvasRef} width={W} height={H} className="notify-canvas" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button type="button" className="btn primary" style={{ flex: 1, minWidth: 180 }} disabled={busy} onClick={send}>
              {busy ? 'Đang gửi…' : (batch ? '📢 Gửi tất cả máy' : '📢 Gửi xuống máy')}
            </button>
            {/* Luôn hiện để dễ tìm; mờ đi khi máy chưa có thông báo nào để gỡ. */}
            {!batch && (
              <button
                type="button"
                className="btn"
                style={{ flex: 'none' }}
                disabled={busy || !hasNotify}
                title={
                  status === null
                    ? 'Đang kiểm tra trạng thái thông báo…'
                    : hasNotify
                      ? 'Đặt lại ảnh nền trơn và bỏ khoá đổi nền'
                      : 'Máy chưa có thông báo nào để gỡ'
                }
                onClick={clear}
              >
                🧹 Gỡ thông báo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* CSS riêng cho modal thông báo — nhúng cùng chỗ với các style modal khác. */
export const NOTIFY_CSS = `
/* Modal này có thể mở TỪ TRONG DeviceModal, nơi rule .dev-drawer .modal biến mọi
   .modal thành panel cao 100vh, bo góc 0. Ghi đè lại (3 class > 2 class) để nó là
   hộp thoại bình thường. */
.modal.notify-modal,.dev-drawer .modal.notify-modal{
  width:620px;max-width:100%;height:auto;max-height:calc(100vh - 80px);overflow-y:auto;
  border:1px solid var(--border2);border-left:1px solid var(--border2);border-radius:14px;
  box-shadow:var(--shadow-lg);animation:scaleIn .28s var(--ease)}
.dev-drawer .modal.notify-modal .tab-body{padding:18px 20px}

.notify-grid{display:flex;gap:16px;align-items:flex-start}
.notify-form{flex:1;min-width:0;display:flex;flex-direction:column;gap:12px}
.notify-form label{display:block}
.notify-form label>span{display:flex;justify-content:space-between;align-items:baseline;
  font-size:13px;font-weight:700;color:var(--text2);margin-bottom:5px}
.notify-form label>span i{font-style:normal;font-weight:600;color:var(--text3);font-size:12px}
/* Input trong modal này không nằm trong .uform nên phải tự khớp style của app. */
.notify-form input,.notify-form textarea{background:var(--bg3);border:1px solid var(--border);
  border-radius:9px;padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;
  outline:none;width:100%;resize:vertical}
.notify-form input:focus,.notify-form textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}
.notify-hint{font-size:12px;color:var(--text3)}
.notify-preview{flex:none;text-align:center}
.notify-preview-label{font-size:12px;font-weight:700;color:var(--text3);margin-bottom:6px}
.notify-canvas{width:150px;height:auto;border-radius:14px;border:1px solid var(--border);
  box-shadow:0 6px 18px rgba(0,0,0,.18);display:block}
@media (max-width:640px){
  .notify-grid{flex-direction:column}
  .notify-preview{align-self:center}
  .notify-canvas{width:132px}
}
`;
